import { inject, Injectable } from '@angular/core';
import { ToastService } from '@insolution/core-ui';
import {
  ConsultExtended,
  ConsultExtendedInput,
  CopyConsultGQL,
  CopyConsultMutation,
  CreateConsultGQL,
  CreateConsultMutation,
  DeleteConsultGQL,
  DeleteConsultMutation,
  EConsultStatus,
  GetConsultByIdGQL,
  GetConsultByIdQuery,
  GetConsultByOrderIdGQL,
  GetConsultByOrderIdQuery,
  ListConsultsByPatientIdGQL,
  ListConsultsByPatientIdQuery,
  ListOpenConsultsByTenantIdGQL,
  ListOpenConsultsByTenantIdQuery,
  SetConsultArchivedGQL,
  SetConsultArchivedMutation,
  UpdateConsultGQL,
  UpdateConsultMutation,
} from '@insolution/graphql-data-access';
import { CurrentTenantService } from '@insolution/tenant-data-access';
import { TranslateService } from '@ngx-translate/core';
import { getCurrentUser } from 'aws-amplify/auth';
import { firstValueFrom, map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ConsultService {
  private createConsultGQL = inject(CreateConsultGQL);
  private copyConsultGQL = inject(CopyConsultGQL);
  private deleteConsultGQL = inject(DeleteConsultGQL);
  private setConsultArchivedGQL = inject(SetConsultArchivedGQL);
  private getConsultByIdGQL = inject(GetConsultByIdGQL);
  private getConsultByOrderIdGQL = inject(GetConsultByOrderIdGQL);
  private updateConsultGQL = inject(UpdateConsultGQL);
  private currentTenantService = inject(CurrentTenantService);
  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);
  private listConsultsByPatientIdGQL = inject(ListConsultsByPatientIdGQL);
  private listOpenConsultsByTenantIdGQL = inject(ListOpenConsultsByTenantIdGQL);

  public isConsultDisabled$(consultId: string): Observable<boolean> {
    return this.watchConsultById$(consultId).pipe(
      map(
        (consult) =>
          !consult || consult.status === EConsultStatus.Closed || consult.status === EConsultStatus.OrderCreated
      )
    );
  }

  public async getConsultById(consultId: string): Promise<ConsultExtended | null> {
    try {
      const result = await firstValueFrom(this.getConsultByIdGQL.fetch({ id: consultId }));

      if (result.errors && result.errors.length > 0) {
        this.toastService.showGeneralErrorToast(result.errors[0].message || 'Fetching consult failed');
        throw new Error(result.errors[0].message || 'Fetching consult failed');
      }

      return result.data?.getConsultById || null;
    } catch (error) {
      this.toastService.showGeneralErrorToast(error instanceof Error ? error.message : undefined);
      console.error('Error fetching consult by ID:', error);
      throw error;
    }
  }

  public async getConsultByOrderId(orderId: string): Promise<GetConsultByOrderIdQuery['getConsultByOrderId']> {
    try {
      const result = await firstValueFrom(this.getConsultByOrderIdGQL.fetch({ id: orderId }));

      if (result.errors && result.errors.length > 0) {
        this.toastService.showGeneralErrorToast(result.errors[0].message || 'Fetching consult failed');
        throw new Error(result.errors[0].message || 'Fetching consult failed');
      }

      return result.data?.getConsultByOrderId;
    } catch (error) {
      this.toastService.showGeneralErrorToast(error instanceof Error ? error.message : undefined);
      console.error('Error fetching consult by order ID:', error);
      throw error;
    }
  }

  public watchConsultById$(consultId: string): Observable<GetConsultByIdQuery['getConsultById'] | null> {
    return this.getConsultByIdGQL
      .watch({ id: consultId })
      .valueChanges.pipe(map((result) => result.data.getConsultById));
  }

  public async updateConsult(
    input: ConsultExtendedInput,
    showToast = true,
    replaceExistingToast = false
  ): Promise<UpdateConsultMutation['updateConsult']> {
    // Remove __typename from all arrays,  will error if you try to submit with for example an `ConsultOccupation` instead of `ConsultOccupationInput`
    const cleanedInput = this.removeTypenameFromArrays(input);
    try {
      const result = await firstValueFrom(
        this.updateConsultGQL.mutate(
          {
            input: cleanedInput,
          },
          {
            update: (cache) => {
              cache.modify({
                fields: {
                  listConsultsByPatientId(existing, { DELETE }) {
                    return DELETE;
                  },
                  listOpenConsultsByTenantId(existing, { DELETE }) {
                    return DELETE;
                  },
                  getConsultById(existing, { DELETE }) {
                    return DELETE;
                  },
                },
              });
            },
          }
        )
      );

      if (result.errors && result.errors.length > 0) {
        this.toastService.showGeneralErrorToast(result.errors[0].message || 'Updating consult failed');
        throw new Error(result.errors[0].message || 'Updating consult failed');
      } else {
        if (showToast) {
          this.toastService.show(
            {
              header: this.translateService.instant('consult.toast.consult-has-been-updated.title'),
              message: 'consult.toast.consult-has-been-updated.text',
              classname: 'bg-primary-subtle',
              delay: replaceExistingToast ? 3000 : 10000,
            },
            replaceExistingToast
          );
        }
      }

      return result.data?.updateConsult;
    } catch (error) {
      this.toastService.showGeneralErrorToast(error instanceof Error ? error.message : undefined);
      console.error('Error updating consult:', error);
      throw error;
    }
  }

  public async createConsult(patientId: string, showToast = true): Promise<CreateConsultMutation['createConsult']> {
    const tenantRef = this.currentTenantService.currentTenantRef();
    const currentUserId = (await getCurrentUser()).signInDetails?.loginId as string;

    if (!tenantRef) {
      throw new Error('No tenant selected');
    }

    try {
      const createConsultResult = await firstValueFrom(
        this.createConsultGQL.mutate(
          {
            input: {
              patient_id: patientId,
              status: EConsultStatus.Draft,
              user_id: currentUserId,
              tenant_ref: tenantRef,
              date: new Date().toISOString(),
              id: crypto.randomUUID(),
            },
          },
          {
            update: (cache) => {
              cache.modify({
                fields: {
                  listConsultsByPatientId(existing, { DELETE }) {
                    return DELETE;
                  },
                  listOpenConsultsByTenantId(existing, { DELETE }) {
                    return DELETE;
                  },
                },
              });
            },
          }
        )
      );

      if (createConsultResult.errors && createConsultResult.errors.length > 0) {
        this.toastService.showGeneralErrorToast(createConsultResult.errors[0].message || 'Creating consult failed');
        throw new Error(createConsultResult.errors[0].message || 'Creating consult failed');
      } else {
        if (showToast) {
          this.toastService.show({
            header: this.translateService.instant('consult.toast.consult-has-been-created.title'),
            message: 'consult.toast.consult-has-been-created.text',
            classname: 'bg-primary-subtle',
            delay: 10000,
          });
        }
      }

      return createConsultResult.data?.createConsult;
    } catch (error) {
      this.toastService.showGeneralErrorToast(error instanceof Error ? error.message : undefined);
      console.error('Error creating consult:', error);
      throw error;
    }
  }

  public watchConsultsByPatientId$(
    patientId: string
  ): Observable<ListConsultsByPatientIdQuery['listConsultsByPatientId'] | undefined> {
    return this.listConsultsByPatientIdGQL
      .watch({ patient_id: patientId })
      .valueChanges.pipe(map((result) => result.data.listConsultsByPatientId));
  }

  public watchOpenConsultsByTenantId$(
    tenantId: string
  ): Observable<ListOpenConsultsByTenantIdQuery['listOpenConsultsByTenantId']> {
    return this.listOpenConsultsByTenantIdGQL
      .watch({ tenant_id: tenantId })
      .valueChanges.pipe(map((result) => result.data.listOpenConsultsByTenantId ?? []));
  }

  private removeTypenameFromArrays<T>(obj: T): T {
    if (Array.isArray(obj)) {
      return obj.map((item) => this.removeTypenameFromArrays(item)) as T;
    } else if (obj !== null && typeof obj === 'object') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { __typename, ...rest } = obj as Record<string, unknown> & { __typename?: string };
      const cleaned: Record<string, unknown> = {};
      for (const key in rest) {
        if (Object.prototype.hasOwnProperty.call(rest, key)) {
          cleaned[key] = this.removeTypenameFromArrays(rest[key]);
        }
      }
      return cleaned as T;
    }
    return obj;
  }

  public async copyConsult(consult_id: string, showToast = true): Promise<CopyConsultMutation['copyConsult']> {
    try {
      const copyConsultResult = await firstValueFrom(
        this.copyConsultGQL.mutate(
          { consult_id },
          {
            update: (cache) => {
              cache.modify({
                fields: {
                  listConsultsByPatientId(existing, { DELETE }) {
                    return DELETE;
                  },
                  listOpenConsultsByTenantId(existing, { DELETE }) {
                    return DELETE;
                  },
                },
              });
            },
          }
        )
      );

      if (copyConsultResult.errors && copyConsultResult.errors.length > 0) {
        this.toastService.showGeneralErrorToast(copyConsultResult.errors[0].message || 'Copying consult failed');
        throw new Error(copyConsultResult.errors[0].message || 'Copying consult failed');
      } else {
        if (showToast) {
          this.toastService.show({
            header: this.translateService.instant('consult.toast.consult-has-been-copied.title'),
            message: 'consult.toast.consult-has-been-copied.text',
            classname: 'bg-primary-subtle',
            delay: 10000,
          });
        }
      }

      return copyConsultResult.data?.copyConsult || [];
    } catch (error) {
      this.toastService.showGeneralErrorToast(error instanceof Error ? error.message : undefined);
      console.error('Error copying consult:', error);
      throw error;
    }
  }

  public async deleteConsult(
    consult_id: string,
    showToast = true
  ): Promise<DeleteConsultMutation['deleteConsult'] | undefined> {
    try {
      const deleteConsultResult = await firstValueFrom(
        this.deleteConsultGQL.mutate(
          { consult_id },
          {
            update: (cache) => {
              cache.modify({
                fields: {
                  listConsultsByPatientId(existing, { DELETE }) {
                    return DELETE;
                  },
                  listOpenConsultsByTenantId(existing, { DELETE }) {
                    return DELETE;
                  },
                },
              });
            },
          }
        )
      );

      if (deleteConsultResult.errors && deleteConsultResult.errors.length > 0) {
        this.toastService.showGeneralErrorToast(deleteConsultResult.errors[0].message || 'Deleting consult failed');
        throw new Error(deleteConsultResult.errors[0].message || 'Deleting consult failed');
      } else {
        if (showToast) {
          this.toastService.show({
            header: this.translateService.instant('consult.toast.consult-has-been-deleted.title'),
            message: this.translateService.instant('consult.toast.consult-has-been-deleted.text'),
            classname: 'bg-primary-subtle',
            delay: 10000,
          });
        }
      }

      return deleteConsultResult.data?.deleteConsult;
    } catch (error) {
      this.toastService.showGeneralErrorToast(error instanceof Error ? error.message : undefined);
      console.error('Error deleting consult:', error);
      throw error;
    }
  }

  public async setConsultArchived(
    consult_id: string,
    archived: boolean,
    showToast = true
  ): Promise<SetConsultArchivedMutation['setConsultArchived'] | undefined> {
    try {
      const setConsultArchivedResult = await firstValueFrom(
        this.setConsultArchivedGQL.mutate(
          { consult_id, archived },
          {
            update: (cache) => {
              cache.modify({
                fields: {
                  listConsultsByPatientId(existing, { DELETE }) {
                    return DELETE;
                  },
                  listOpenConsultsByTenantId(existing, { DELETE }) {
                    return DELETE;
                  },
                  getConsultById(existing, { DELETE }) {
                    return DELETE;
                  },
                },
              });
            },
          }
        )
      );

      if (setConsultArchivedResult.errors && setConsultArchivedResult.errors.length > 0) {
        this.toastService.showGeneralErrorToast(
          setConsultArchivedResult.errors[0].message || 'Updating consult failed'
        );
        throw new Error(setConsultArchivedResult.errors[0].message || 'Updating consult failed');
      } else {
        if (showToast) {
          const toastKey = archived
            ? 'consult.toast.consult-has-been-archived'
            : 'consult.toast.consult-has-been-activated';
          this.toastService.show({
            header: this.translateService.instant(toastKey + '.title'),
            message: toastKey + '.text',
            classname: 'bg-primary-subtle',
            delay: 10000,
          });
        }
      }

      return setConsultArchivedResult.data?.setConsultArchived;
    } catch (error) {
      this.toastService.showGeneralErrorToast(error instanceof Error ? error.message : undefined);
      console.error('Error updating consult archived state:', error);
      throw error;
    }
  }
}
